import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { SubHeader } from './ThreeRowsHeader';
import { Logo } from '../header/Logo';
import { TranslatedFontSize } from '../../../../../theming/fontSizeTranslator';
import { useTheme } from '../../../../../theming/useTheme';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { NavigationLink } from '../oldHeader/primaryHeader/PrimaryHeader';
import { Link } from '../../../../partials/link/Link';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { MenuData } from '../header/RowWithFixedNav';
import { AdditionalStyleSheet } from '../../../../components.utils';
import { HamburgerMenu } from './HamburgerMenu';
import { ActionsBar } from './ActionsBar';
import { componentConfigThemeKey as threeRowsHeaderConfigThemeKey, ThreeRowsHeaderThemeProps } from './ThreeRowsHeader.theme';
// import { isCurrentPage } from './header.util';
import { MegaMenu } from './megaMenu';


interface SecondRowHeaderProps {
    subHeader: SubHeader;
    menu: MenuData | null;
    showAccessibilityIcon?: boolean;
    feedName?: string;
}

const getSecondRowStyle = (secondLineBackgroundColor: string, secondLineTextColor: string) => {
  return StyleSheet.create({
    wrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0 0 0 16px',
      height: '48px',
      textTransform: 'uppercase',
      backgroundColor: secondLineBackgroundColor,
    },
    logoAndHamburgerContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    linksList: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      columnGap: '16px',
      listStyle: 'none',
      whiteSpace: 'nowrap',
      padding: '0 20px 0 0',
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      '::-webkit-scrollbar': {
        display: 'none',
      },
    },
    link: {
      color: secondLineTextColor,
      height: '48px',
      display: 'flex',
      flexWrap: 'wrap',
      position: 'relative',
      alignItems: 'center',
      textDecoration: 'none',
      ':hover + span': {
        display: 'block',
      },
    },
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getIndicatorDisplayStyle = (navigationLink: NavigationLink, secondLineSelectedIndicatorColor: string, feedName?: string) => {
  return StyleSheet.create({
    indicator: {
      height: '3px',
      backgroundColor: secondLineSelectedIndicatorColor,
      width: '100%',
      bottom: '0',
      position: 'absolute',
      // display: isCurrentPage(navigationLink, feedName) ? 'block' : 'none',
      display: true ? 'block' : 'none',
    },
  }).indicator;
};

const createMenuStyle = (): AdditionalStyleSheet => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none',
      },
    },
  }) as AdditionalStyleSheet;
};

export const SecondRowHeader: React.FunctionComponent<SecondRowHeaderProps> = props => {
  const { subHeader, menu, showAccessibilityIcon, feedName } = props;
  const { logoForLargeScreen, links, megaMenu } = subHeader;
  const { fontSizesStyles }: { fontSizesStyles: TranslatedFontSize } = useTheme();
  const fontStyle = getStyleBySize(fontSizesStyles, { fontSizeSmall: TYPOGRAPHY_SIZE.BIG, fontSizeMedium: TYPOGRAPHY_SIZE.BIG, fontSizeLarge: TYPOGRAPHY_SIZE.BIG });
  const {
    secondLineBackgroundColor,
    secondLineTextColor,
    secondLineSelectedIndicatorColor } = useTheme<ThreeRowsHeaderThemeProps>(threeRowsHeaderConfigThemeKey);
  const style = getSecondRowStyle(secondLineBackgroundColor, secondLineTextColor);

  return (
    <div className={css(style.wrapper, fontStyle)}>
      <div className={css(style.logoAndHamburgerContainer)}>
        {menu && <HamburgerMenu iconColor={secondLineTextColor} links={menu.links} additionalStyle={createMenuStyle()} />}
        { logoForLargeScreen && (
        <Logo
          {...logoForLargeScreen}
          logoHeightInLargeScreen={1.3}
          logoHeightInMediumScreen={1.3}
          logoHeightInSmallScreen={1.3}
        />
        )}
      </div>
      {links && (
      <ul className={css(style.linksList)}>
        {links.map((navigationLink: NavigationLink, index: number) => {
          const indicatorStyle = getIndicatorDisplayStyle(navigationLink, secondLineSelectedIndicatorColor, feedName);
          return (
            <li key={index} className={css(style.link)}>
              <Link className={css(style.link, fontStyle)} href={navigationLink.link.href}>
                <span>{navigationLink.link.text}</span>
              </Link>
              <span className={css(indicatorStyle)} />
            </li>
          );
        })}
        {megaMenu && <MegaMenu moreIconColor={secondLineTextColor} megaMenu={megaMenu} />}
      </ul>
      )}
      <ActionsBar iconColor={secondLineTextColor} showAccessibilityIcon={showAccessibilityIcon} />
    </div>
  );
};
